import { z } from 'zod';

import {
  BaseFlowBlockSchema,
  BlockType,
  type ConsumerOf,
  ConsumerOfExternalDocumentPacketSchema,
  type JustSourceBlockName,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';

export type ConsumerOfLabelboxAnnotations = ConsumerOf;

export const ConsumerOfLabelboxAnnotationsSchema = z
  .object({
    sourceBlockName: z.string().min(1, { message: 'Document Annotations is required' }),
  })
  .transform(({ sourceBlockName }: { sourceBlockName: string }) => ({
    sourceBlockName,
    consumesType: ProducedType.LabelboxAnnotations,
  })) as z.ZodType<ConsumerOfLabelboxAnnotations, z.ZodTypeDef, JustSourceBlockName>;

// Matches the backend schema at
// backend/flow/api/src/temporal/blocks/sendToLabelboxForReview/config.ts
//
// Also matches the input format for the predict/labelbox client at
// backend/primary/src/clients/predict/labelbox.ts

// For adding custom classifications to labelbox datarows, see also:
// https://github.com/VivaaHealth/vivaa/blob/8332a045f42335156523b7a5882c2ee880980d1c/backend/primary/src/clients/predict/labelbox.ts#L50-L54
// https://docs.labelbox.com/reference/import-image-annotations#classifications
//
// Some contortion is needed to support nested classifications using z.lazy.
// From https://github.com/colinhacks/zod?tab=readme-ov-file#recursive-types:
// > You can define a recursive schema in Zod, but because of a limitation of TypeScript, their type can't be statically inferred.
// > Instead you'll need to define the type definition manually, and provide it to Zod as a "type hint".
interface LabelboxClassification {
  name?: string;
  classifications?: LabelboxClassification[];
  value: string | z.infer<typeof classificationValueSchema>;
}

const labelboxClassificationSchema: z.ZodType<LabelboxClassification> = z.object({
  name: z.string().optional(),
  classifications: z.lazy(() => labelboxClassificationSchema.array().optional()),
  value: z.lazy(() => z.union([z.string(), classificationValueSchema])),
});

export const labelboxAnnotationsSchema = z.array(labelboxClassificationSchema);

const classificationAnswerSchema = z.object({
  name: z.string().optional(),
  classifications: labelboxAnnotationsSchema.optional(),
});

const classificationValueSchema = z.object({
  name: z.literal('checklist').optional(),
  answer: z.union([z.string(), classificationAnswerSchema, classificationAnswerSchema.array()]),
});

export type LabelboxAnnotations = z.infer<typeof labelboxAnnotationsSchema>;

export const SendToLabelboxForReviewConfigSchema = z.object({
  returnType: returnTypeSchema(ProducedType.LabelboxReviewResult),
  externalDocumentPacket: ConsumerOfExternalDocumentPacketSchema,
  documentAnnotations: ConsumerOfLabelboxAnnotationsSchema,
  labelboxProjectId: z.string().optional(),
  labelboxDatasetId: z.string().optional(),
});

export type SendToLabelboxForReviewConfig = z.infer<typeof SendToLabelboxForReviewConfigSchema>;

export const SendToLabelboxForReviewBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.SendToLabelboxForReview),
  config: SendToLabelboxForReviewConfigSchema,
  returnType: returnTypeSchema(ProducedType.LabelboxReviewResult),
});
export type SendToLabelboxForReviewBlock = z.infer<typeof SendToLabelboxForReviewBlockSchema>;

// matches interfaces at
// web/vivaa-api: web/vivaa-api/src/models/flow/sendToLabelboxForReview/configs.ts
// flow-api: backend/flow/api/src/executor/flowValueTypes/labelboxReviewResult.ts
// primary: backend/primary/src/webhooks/incoming/labelbox/referral.ts
export interface LabelboxReviewResult {
  organizationId: string;
  projectId: string;
  actorId: string;
  timestamp: string;
  dataRows: {
    id: string;
    globalKey: string;
    metadataFields: {
      name: string;
      value: string;
    }[];
    labels: {
      classifications: {
        name: string;
        value: string[];
      }[];
    }[];
  }[];
}
